import React, { useMemo, useState } from "react";
import {
  Card,
  Button,
  Input,
  Link,
  Unlink,
  AlertTriangle,
  MapPin,
  Phone,
  Clock3,
  Lock,
  StatusDropdown,
} from "../../../ui";
import { APPS, DAYS, OPEN_STATUSES, tx } from "../../ludConnect.constants";
import { normalizeIntegrationKey } from "../../../../utils/integrations";
import { fmtDate, fmtDateShort } from "../../helpers/ludConnect.helpers";
import "./Branches.css";
import { useAccess } from "../../../../access/hooks/useAccess";
// Latest non-deleted link/disconnect request per "type|branchId|appId" for one
// restaurant. Built once per requests change instead of filtering + sorting
// every request for every branch × app on each render.
const indexLatestConnectionRequests = (requests, restaurantId) => {
  const latest = new Map();
  requests.forEach((r) => {
    if (
      r.deleted ||
      (r.type !== "link_app" && r.type !== "disconnect_app") ||
      (r.storeCode !== restaurantId && r.payload.restId !== restaurantId)
    )
      return;
    const key = `${r.type}|${r.payload.branchId}|${r.payload.appId}`;
    const current = latest.get(key);
    if (!current || new Date(r.createdAt) > new Date(current.createdAt))
      latest.set(key, r);
  });
  return latest;
};

const stateFor = (latestRequests, branch, appId, ar) => {
  if (branch.closedAt) return { id: "closed", label: tx("مغلق", "Closed", ar) };
  const link = latestRequests.get(`link_app|${branch.id}|${appId}`);
  const disc = latestRequests.get(`disconnect_app|${branch.id}|${appId}`);
  if (disc && OPEN_STATUSES.includes(disc.status))
    return {
      id: "close_request",
      label: tx("قيد الإيقاف", "Disconnecting", ar),
    };
  if (link && OPEN_STATUSES.includes(link.status))
    return {
      id: link.status,
      label: {
        draft: tx("مسودة", "Draft", ar),
        sent: tx("قيد الانتظار", "Waiting", ar),
        req_in_progress: tx("قيد المعالجة", "In progress", ar),
        pending_confirm: tx("بانتظار الاعتماد", "Pending confirmation", ar),
        clarify: tx("يتطلب توضيح", "Clarification required", ar),
      }[link.status],
    };
  if (link?.status === "done")
    return { id: "active", label: tx("نشط", "Active", ar) };
  if (link?.status === "rejected")
    return { id: "rejected", label: tx("مرفوض", "Rejected", ar) };
  const connectionStatus = branch.mergedApps?.[appId]?.status || "new";
  const labels = {
    new: tx("جديد", "New", ar),
    active: tx("نشط", "Active", ar),
    issue: tx("مشكلة", "Issue", ar),
    closed: tx("مغلق", "Closed", ar),
    in_progress: tx("قيد المعالجة", "In progress", ar),
  };
  return {
    id: connectionStatus,
    label: labels[connectionStatus] || connectionStatus,
  };
};

const findApp = (appId) => {
  const key = normalizeIntegrationKey(appId) || String(appId || "").toLowerCase();
  return APPS.find((app) => app.id === key);
};

const formatWorkTime = (workTime = [], dayId) => {
  const activeSchedule = workTime[0];
  const day = activeSchedule?.days?.[dayId];
  if (!day) return "—";
  const periods = [day.morning, day.evening]
    .filter((period) => period?.from && period?.to)
    .map((period) => `${period.from} - ${period.to}`);
  return periods.length ? periods.join(" / ") : "—";
};
const Branches = ({
  ar,
  restaurant,
  isProvider,
  canManage,
  pendingActions,
  requests,
  onBranchField,
  onAppField,
  onAddApp,
  onLink,
  onDisconnect,
  onIssue,
  onActivate,
  onClose,
}) => {
  const { has } = useAccess();
  // display only: the handlers in LudConnect re-check per restaurant
  const canEditConnections = has("ludConnect.manage");
  const [open, setOpen] = useState({});
  const [hours, setHours] = useState({});
  const adminIds = Object.keys(restaurant.adminApps || {});
  const latestRequests = useMemo(
    () => indexLatestConnectionRequests(requests, restaurant.id),
    [requests, restaurant.id],
  );

  return (
    <Card
      title={
        <span>
          <MapPin size={16} />{" "}
          {tx(
            "الفروع — حالة الربط والتواريخ",
            "Branches — connection status and dates",
            ar,
          )}
        </span>
      }
      hoverable={false}
      className="lud-connect__section-card"
    >
      {restaurant.branches.map((branch) => {
        const active = Object.keys(branch.mergedApps || {}).filter(
          (id) => stateFor(latestRequests, branch, id, ar).id === "active",
        );
        return (
          <div className="lud-connect__branch" key={branch.id}>
            <button
              type="button"
              className="lud-connect__branch-summary"
              aria-expanded={!!open[branch.id]}
              onClick={() =>
                setOpen((x) => ({ ...x, [branch.id]: !x[branch.id] }))
              }
            >
              <span>
                <b>
                  <MapPin size={14} /> {branch.name}
                </b>
                <small>
                  {active.length
                    ? active.map((id) => (
                        <i key={id}>
                          {ar
                            ? findApp(id)?.name || id
                            : findApp(id)?.nameEn || id}
                        </i>
                      ))
                    : tx(
                        "لا يوجد تطبيقات فعّالة",
                        "No active applications",
                        ar,
                      )}
                </small>
              </span>
              <span>
                {branch.activatedAt && (
                  <em className="good">
                    {tx("تفعّل", "Activated", ar)}:{" "}
                    {fmtDateShort(branch.activatedAt, ar)}
                  </em>
                )}
                {branch.closeRequestedAt && !branch.closedAt && (
                  <em className="warn">
                    {tx("طلب إغلاق", "Closure requested", ar)}:{" "}
                    {fmtDateShort(branch.closeRequestedAt, ar)}
                  </em>
                )}
                {branch.closedAt && (
                  <em className="bad">
                    {tx("أُغلق", "Closed", ar)}: {fmtDateShort(branch.closedAt, ar)}
                  </em>
                )}
                <b aria-hidden="true">{open[branch.id] ? "▴" : "▾"}</b>
              </span>
            </button>
            {open[branch.id] && (
              <div className="lud-connect__branch-body">
                <div className="lud-connect__branch-info">
                  <div>
                    <MapPin size={14} />
                    {branch.location ? (
                      <a
                        href={branch.location}
                        target="_blank"
                        rel="noreferrer"
                      >
                        {branch.location}
                      </a>
                    ) : (
                      <span>—</span>
                    )}
                  </div>
                  <div>
                    <Phone size={14} />
                    <a href={branch.phone ? `tel:${branch.phone}` : undefined}>
                      {branch.phone || "—"}
                    </a>
                  </div>
                </div>
                <Button
                  appearance="text"
                  size="sm"
                  onClick={() =>
                    setHours((x) => ({ ...x, [branch.id]: !x[branch.id] }))
                  }
                  icon={<Clock3 size={14} />}
                >
                  {hours[branch.id]
                    ? tx("إخفاء أوقات العمل", "Hide working hours", ar)
                    : tx("عرض أوقات العمل", "Show working hours", ar)}
                </Button>
                {hours[branch.id] && (
                  <div className="lud-connect__hours">
                    {DAYS.map((d) => (
                      <div key={d.id}>
                        <span>{ar ? d.label : d.labelEn}</span>
                        <span>{formatWorkTime(branch.workTime, d.id)}</span>
                      </div>
                    ))}
                  </div>
                )}
                <div className="lud-connect__branch-login">
                  <b>
                    <Lock size={14} />{" "}
                    {tx(
                      "بيانات دخول الفرع (لكل التطبيقات)",
                      "Branch login details (for all applications)",
                      ar,
                    )}
                  </b>
                  <Input
                    value={branch.email || ""}
                    placeholder={tx("الإيميل", "Email", ar)}
                    disabled={!isProvider}
                    onChange={(e) =>
                      onBranchField(branch.id, "email", e.target.value)
                    }
                  />
                  <Input
                    value={branch.pass || ""}
                    placeholder={tx("الرقم السري", "Password", ar)}
                    disabled={!isProvider}
                    onChange={(e) =>
                      onBranchField(branch.id, "pass", e.target.value)
                    }
                  />
                </div>
                {branch.credAddedAt && (
                  <div className="lud-connect__date-line">
                    {tx("أُضيفت بيانات الدخول", "Login details added", ar)}:{" "}
                    {fmtDate(branch.credAddedAt, ar)}
                  </div>
                )}
                <div className="lud-connect__branch-apps">
                  {!adminIds.length ? (
                    <div className="lud-connect__muted">
                      {tx(
                        'لا يوجد تطبيقات مُضافة كأدمن على مستوى المطعم بعد — أضفها من بوكس "أدمن التطبيقات" فوق.',
                        'No applications have been added as restaurant-level admins yet — add them in the "Application admins" box above.',
                        ar,
                      )}
                    </div>
                  ) : (
                    adminIds.map((appId) => {
                      const app = findApp(appId);
                      if (!app) {
                        return null;
                      }
                      const conn = branch.mergedApps?.[appId];
                      
                      if (!conn)
                        return canManage ? (
                          <div className="lud-connect__app-row add" key={appId}>
                            <b>{ar ? app.name : app.nameEn}</b>
                            <Button
                              size="sm"
                              variant="neutral"
                              disabled={pendingActions?.has(`add-app:${branch.id}:${appId}`)}
                              onClick={() => onAddApp(branch.id, appId)}
                            >
                              {tx("إضافة لهذا الفرع", "Add to this branch", ar)}
                            </Button>
                          </div>
                        ) : null;
                      const state = stateFor(latestRequests, branch, appId, ar);
                      const linking = pendingActions?.has(
                        `link:${branch.id}:${appId}`,
                      );
                      const canLink =
                        canManage &&
                        !linking &&
                        conn.credId &&
                        conn.appBranchName &&
                        !OPEN_STATUSES.includes(state.id) &&
                        state.id !== "active" &&
                        state.id !== "closed";
                      const canDisc = canManage && state.id === "active";
                      return (
                        <div className="lud-connect__app-row" key={appId}>
                          <div className="lud-connect__app-row-top">
                            <b>{ar ? app.name : app.nameEn}</b>
                            {canEditConnections ? (
                              <StatusDropdown
                                className="lud-connect__status-select"
                                value={conn.status || state.id || "new"}
                                options={[
                                  "new",
                                  "in_progress",
                                  "active",
                                  "issue",
                                  "closed",
                                ]}
                editable={false}
                                getLabel={(value) => ({
                                  new: tx("جديد", "New", ar),
                                  in_progress: tx("قيد المعالجة", "In progress", ar),
                                  active: tx("نشط", "Active", ar),
                                  issue: tx("مشكلة", "Issue", ar),
                                  closed: tx("مغلق", "Closed", ar),
                                })[value] || value}
                                onChange={(value) =>
                                  onAppField(
                                    branch.id,
                                    appId,
                                    "status",
                                    value,
                                  )
                                }
                              />
                            ) : (
                              <span className={`lud-connect__status ${state.id}`}>
                                {state.label}
                              </span>
                            )}
                          </div>
                          <div className="lud-connect__app-fields">
                            <Input
                              value={conn.appBranchName || ""}
                              placeholder={tx(
                                "اسم الفرع بالتطبيق",
                                "Branch name in application",
                                ar,
                              )}
                              disabled={!canManage}
                              onChange={(e) =>
                                onAppField(
                                  branch.id,
                                  appId,
                                  "appBranchName",
                                  e.target.value,
                                )
                              }
                            />
                            <Input
                              value={conn.credId || ""}
                              placeholder={tx("المعرف", "ID", ar)}
                              disabled={!canManage}
                              onChange={(e) =>
                                onAppField(
                                  branch.id,
                                  appId,
                                  "credId",
                                  e.target.value,
                                )
                              }
                            />
                          </div>
                          {canManage && (
                            <div className="lud-connect__app-actions">
                              <Button
                                size="sm"
                                disabled={!canLink}
                                icon={<Link size={14} />}
                                loading={linking}
                                onClick={() => onLink(branch.id, appId)}
                              >
                                {tx("ربط", "Link", ar)}
                              </Button>
                              <Button
                                size="sm"
                                variant={canDisc ? "danger" : "neutral"}
                                disabled={!canDisc}
                                icon={<Unlink size={14} />}
                                onClick={() => onDisconnect(branch.id, appId)}
                              >
                                {tx("إيقاف", "Disconnect", ar)}
                              </Button>
                              <Button
                                size="sm"
                                variant="warning"
                                icon={<AlertTriangle size={14} />}
                                onClick={() => onIssue(branch.id, appId)}
                              >
                                {tx("مشكلة", "Issue", ar)}
                              </Button>
                            </div>
                          )}
                        </div>
                      );
                    })
                  )}
                </div>
                {canManage && (
                  <>
                    <Button
                      size="sm"
                      variant="success"
                      fullWidth
                      className="lud-connect__app-actions"
                      disabled={!!branch.activatedAt}
                      onClick={() => onActivate(branch.id)}
                    >
                      {branch.activatedAt
                        ? tx("الفرع مفعّل", "Branch active", ar)
                        : tx(
                            "طلب تفعيل الفرع",
                            "Request branch activation",
                            ar,
                          )}
                    </Button>
                    <Button
                      size="sm"
                      variant="danger"
                      appearance="filled"
                      fullWidth
                      className="lud-connect__app-actions"
                      disabled={!!branch.closedAt || !!branch.closeRequestedAt}
                      onClick={() => onClose(branch.id)}
                    >
                      {branch.closedAt
                        ? tx("الفرع مُغلق", "Branch closed", ar)
                        : branch.closeRequestedAt
                          ? tx(
                              "طلب الإغلاق قيد المعالجة",
                              "Closure request in progress",
                              ar,
                            )
                          : tx(
                              "طلب إغلاق الفرع بالكامل",
                              "Request full branch closure",
                              ar,
                            )}
                    </Button>
                  </>
                )}
              </div>
            )}
          </div>
        );
      })}
    </Card>
  );
};
export default Branches;
